import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { CreditApplicationDTO, fetchApplicationsByUser } from '../../services/creditService';
import '../../styles/AccountTools.css';

const CreditApplications = () => {
  const { user } = useAuth();
  const userId = user?.id || '';
  const [applications, setApplications] = useState<CreditApplicationDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  const loadApplications = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const page = await fetchApplicationsByUser(userId);
      setApplications(page.content || []);
      setMessage('');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Failed to load applications.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  if (!userId) {
    return (
      <main className="tool-page">
        <div className="tool-card tool-empty">Sign in to view credit applications.</div>
      </main>
    );
  }

  return (
    <main className="tool-page">
      <div className="tool-header">
        <div>
          <h1>Your Credit Applications</h1>
          <p>Review submitted credit applications and issued credits.</p>
        </div>
        <div className="tool-actions">
          <Link className="tool-btn-secondary" to="/account/credits">Credits</Link>
          <Link className="tool-btn" to="/account/credits/apply">Apply</Link>
        </div>
      </div>

      {message && <div className="tool-message error">{message}</div>}

      <section className="tool-card">
        {loading ? (
          <div className="tool-loading">Loading applications...</div>
        ) : applications.length === 0 ? (
          <div className="tool-empty">No applications yet.</div>
        ) : (
          <div className="tool-list">
            {applications.map((application) => (
              <Link className="tool-row" key={application.id} to={`/account/credits/applications/${application.id}`}>
                <div className="tool-row-main">
                  <div className="tool-row-title">Application</div>
                  <div className="tool-row-sub">
                    {(application.purposes || []).map((purpose) => purpose.name).join(', ') || application.description || 'No description'}
                  </div>
                  <div className="tool-row-sub">
                    Created {application.createdAt ? new Date(application.createdAt).toLocaleString() : 'not available'}
                  </div>
                </div>
                <span className={`tool-pill ${application.status === 'DENIED' ? 'danger' : application.status === 'PENDING' ? 'warning' : ''}`}>
                  {application.status || 'UNKNOWN'}
                </span>
              </Link>
            ))}
          </div>
        )}
      </section>
    </main>
  );
};

export default CreditApplications;
